import React, { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import VendorGraphicsHeader from "@/components/vendor-graphics/VendorGraphicsHeader";
import VendorGraphicsSidebar from "@/components/vendor-graphics/VendorGraphicsSidebar";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { userHasAnyRole } from "@/lib/userRoles";

const VendorGraphicsDashboardLayout: React.FC = () => {
  console.log("✅ VendorGraphicsDashboardLayout mounted");
  
  const { user, profile, isLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [hasGraphicsRole, setHasGraphicsRole] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setHasGraphicsRole(false); return; }
      // Authorization from user_roles, not profiles.role / membership slug.
      const allowed = await userHasAnyRole(user.id, ["vendor_graphics", "admin"]);
      if (!cancelled) setHasGraphicsRole(allowed);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  console.log("🔍 VendorGraphicsDashboardLayout - Auth state:", { 
    hasUser: !!user, 
    userRole: profile?.role, 
    isLoading 
  });

  useEffect(() => {
    console.log("🔄 VendorGraphicsDashboardLayout useEffect - checking auth");
    
    // Check if user is authenticated and has vendor_graphics membership
    if (!isLoading) {
      console.log("✅ Auth loading complete, checking permissions");
      
      if (!user) {
        console.log("❌ No user found, redirecting to login");
        navigate('/auth/login', { state: { from: '/vendor-graphics-dashboard' } });
        return;
      }
      
      if (hasGraphicsRole === null) return;
      if (!hasGraphicsRole) {
        console.log("❌ User does not have vendor_graphics membership or graphics/vendor_graphics role, redirecting to dashboard");
        toast.error("You don't have graphics vendor privileges to access this area");
        navigate('/dashboard');
        return;
      }
      
      console.log("✅ Graphics vendor access confirmed");
    }
  }, [user, hasGraphicsRole, isLoading, navigate]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Show loading state while checking authentication
  if (isLoading || hasGraphicsRole === null) {
    console.log("⏰ VendorGraphicsDashboardLayout showing loading state");
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-lg font-medium">Loading...</span>
      </div>
    );
  }

  // If not vendor_graphics membership or graphics/vendor_graphics role, the useEffect will handle the redirect
  if (!user || !hasGraphicsRole) {
    console.log("🚫 VendorGraphicsDashboardLayout - No access, returning null");
    return null;
  }

  console.log("🎯 VendorGraphicsDashboardLayout rendering main layout");

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <VendorGraphicsHeader toggleSidebar={toggleSidebar} />
      <div className="flex flex-1">
        <VendorGraphicsSidebar isOpen={sidebarOpen} />
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default VendorGraphicsDashboardLayout;